'use client';

import { FC, memo } from 'react';
import { Switch, TextField, MenuItem } from '@mui/material';
import { useTranslations } from 'next-intl';
import { ArrowDown } from '@/components';
import { WlasParamDef } from '@/features/pages/profile/wlas-widget/components/WlasTabsPanel/tabs/configuration/constants';
import { WlasValues } from '@/features/pages/profile/wlas-widget/components/WlasTabsPanel/tabs/configuration/utils';
import { ParamControl, ParamDesc, ParamLabel, ParamLabelGroup, ParamRow } from './styled';
import { WlasColorInput } from './components/WlasColorInput';

interface WlasParamRowProps {
  param: WlasParamDef;
  values: WlasValues;
  onChange: (id: string, value: string | boolean) => void;
}

export const WlasParamRow: FC<WlasParamRowProps> = memo(({ param, values, onChange }) => {
  const t = useTranslations('wlasWidget');

  const currentValue = values[param.id];

  const renderControl = () => {
    if (param.type === 'bool') {
      return (
        <Switch
          size="small"
          checked={!!currentValue}
          onChange={(e) => onChange(param.id, e.target.checked)}
          inputProps={{ 'aria-label': t(param.descriptionKey + '.label') }}
        />
      );
    }

    if (param.type === 'color') {
      return (
        <WlasColorInput
          value={String(currentValue ?? '')}
          onChange={(v) => onChange(param.id, v)}
          label={t(param.descriptionKey + '.label')}
        />
      );
    }

    if (param.type === 'select') {
      return (
        <TextField
          select
          size="small"
          value={String(currentValue ?? '')}
          onChange={(e) => onChange(param.id, e.target.value)}
          inputProps={{ 'aria-label': t(param.descriptionKey + '.label') }}
          sx={{ minWidth: 140 }}
          slotProps={{
            select: {
              displayEmpty: true,
              IconComponent: (props) => <ArrowDown {...props} />,
              MenuProps: { disableScrollLock: true, PaperProps: { style: { maxHeight: 220 } } },
            },
          }}
        >
          {(param.options ?? []).map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label.startsWith('selectOptions.') ? t(opt.label) : opt.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (param.type === 'text') {
      const placeholder =
        param.id === 'member-unique-code'
          ? t('params.memberUniqueCode.placeholder')
          : param.skipEmpty
            ? t('optional')
            : undefined;
      return (
        <TextField
          size="small"
          value={String(currentValue ?? '')}
          onChange={(e) => onChange(param.id, e.target.value)}
          placeholder={placeholder}
          inputProps={{ 'aria-label': t(param.descriptionKey + '.label') }}
          sx={{ width: 160 }}
        />
      );
    }

    const _exhaustive: never = param.type;
    return null;
  };

  return (
    <ParamRow>
      <ParamLabelGroup>
        <ParamLabel>{t(param.descriptionKey + '.label')}</ParamLabel>
        <ParamDesc>{t(param.descriptionKey + '.desc')}</ParamDesc>
      </ParamLabelGroup>

      <ParamControl>{renderControl()}</ParamControl>
    </ParamRow>
  );
}) as FC<WlasParamRowProps>;
WlasParamRow.displayName = 'WlasParamRow';
